import { Flex, LoadingSpinner, Grid, Text, Heading } from '@homebotapp/hb-react-component-catalog'
import { ListingCardNew, TagPreferenceType } from '../ListingCardNew/ListingCardNew'
import { FeedBase, FeedQuery, FeedType, ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'
import { defineMessages, useIntl } from 'react-intl'
import { FeedEmptyState } from '../../FeedEmptyState/FeedEmptyState'
import { HOME_SEARCH_FOR_YOU_PAGE } from '../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { GalleryTypeWithAlertToggle } from '../../../features/ForYouContent/components/GalleryTypeWithAlertToggle'
import useViewport from '../../../hooks/useViewport'
import { useMemo } from 'react'

interface GalleryListProps {
  listings?: ListingCompact[]
  isLoading?: boolean
  /** optional; allows for impersonation of favorite listings */
  clientId?: string
  feedType: FeedType
  refetch: () => void
  feed: FeedBase
  data: FeedQuery
}

export const MSG = defineMessages({
  noListingFound: {
    id: 'General.noListingFound',
    defaultMessage: 'No listings found'
  },
  newlyListed: {
    id: 'Gallery.newlyListed',
    defaultMessage: 'Newly Listed'
  },
  recentlySold: {
    id: 'Gallery.recentlySold',
    defaultMessage: 'Recently Sold'
  },
  openHouses: {
    id: 'Gallery.openHouses',
    defaultMessage: 'Open Houses'
  },
  assumables: {
    id: 'Gallery.assumables',
    defaultMessage: 'Assumable Loans'
  },
  concessions: {
    id: 'Gallery.concessions',
    defaultMessage: 'Seller Concessions'
  },
  recentPriceDrop: {
    id: 'Gallery.recentPriceDrop',
    defaultMessage: 'Recent Price Drop'
  },
  updatedToday: {
    id: 'Gallery.updatedToday',
    defaultMessage: 'Updated today'
  }
})

const feedTypeToTagMap: Partial<Record<FeedType, TagPreferenceType>> = {
  [FeedType.OpenHouses]: 'openHouse',
  [FeedType.Assumables]: 'assumableRate',
  [FeedType.RecentPriceDrop]: 'priceChange',
  [FeedType.Concessions]: 'concession'
}

export const GalleryList = ({ listings, isLoading, clientId, feedType, refetch, feed, data }: GalleryListProps) => {
  const intl = useIntl()
  const viewport = useViewport()

  const preferredTag = feedTypeToTagMap[feedType]

  const galleryList = useMemo(() => {
    return (
      <Grid py={[1, null, 0]} gridAutoFlow='column' gridAutoColumns='260px' gap={4} minWidth='max-content'>
        {listings?.map(listing => (
          <ListingCardNew
            key={listing.id}
            listing={listing}
            clientId={clientId}
            tagPreference={preferredTag}
            tracking={{
              guid: 'pgv1GYh9KWrN1iLBCdrc3W',
              ui_context: HOME_SEARCH_FOR_YOU_PAGE,
              descriptor: 'Gallery Listing Clicked'
            }}
          />
        ))}
      </Grid>
    )
  }, [])

  if (isLoading) {
    return <LoadingSpinner variant='bot-head' size='lg' position='relative' />
  }

  if (!listings?.length) {
    return <FeedEmptyState feedType={feedType} />
  }

  return (
    <Flex ml={[0, 0]} mb={[0, 4]} gap={3} width='100%'>
      <Flex flexShrink={0}>
        <GalleryTypeWithAlertToggle
          data={data}
          feed={feed}
          refetch={refetch}
          updateFrequency={intl.formatMessage(MSG.updatedToday)}
          title={intl.formatMessage(MSG?.[feedType])}
        >
          {!viewport.isLarge && (
            <Flex
              py={1}
              pl={1}
              flexDirection='column'
              gap={4}
              width='100%'
              overflowX='auto'
              overflowY='hidden'
              paddingBottom={viewport.isSmall ? 4 : 0}
            >
              {galleryList}
            </Flex>
          )}
        </GalleryTypeWithAlertToggle>
      </Flex>
      {viewport.isLarge && (
        <Flex py={1} pl={1} flex='1' overflowX='auto' overflowY='hidden'>
          {galleryList}
        </Flex>
      )}
    </Flex>
  )
}
